<template>
  <div class="flex flex-col gap-3 min-h-[200px]">
    <div v-if="!is_fusion_admin" class="px-2 py-8 text-center text-sm text-text-subtle">
      {{ $t('scheduled.admin.adminOnly') }}
    </div>

    <template v-else>
      <div class="text-sm text-text-subtle">
        {{ $t('scheduled.admin.intro') }}
      </div>

      <!-- Dealer picker -->
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-text-default">{{ $t('scheduled.admin.storeLabel') }}</span>
        <fusion-dropdown
          v-model="selected_dealer"
          :options="dealer_options"
          sort-order="none"
          compact
          @change="loadDealer"
        />
      </label>

      <!-- Learned brief facts for this store. Under the picker because it is store-scoped like the
           controls above it, and only once a store is chosen — there is nothing to approve otherwise. -->
      <brief-fact-approvals v-if="selected_dealer" :dealer-id="selected_dealer" />

      <!-- Store-wide actions -->
      <div v-if="selected_dealer && tasks.length" class="flex items-center gap-2">
        <button
          class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link"
          :disabled="processing"
          @click="setAllEnabled(true)"
        >{{ $t('scheduled.admin.enableAll') }}</button>
        <button
          class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link"
          :disabled="processing"
          @click="setAllEnabled(false)"
        >{{ $t('scheduled.admin.disableAll') }}</button>
        <span class="text-xs text-text-subtle">{{ $t('scheduled.admin.taskCount', { count: tasks.length }) }}</span>
        <label class="ml-auto flex items-center gap-1.5 text-xs text-text-default cursor-pointer">
          <input v-model="watchers_only" type="checkbox" class="cursor-pointer" />
          {{ $t('scheduled.admin.watchersOnly') }}
        </label>
      </div>

      <!-- Health summary. An admin opens this page to find what is wrong, so the counts that mean
           "something needs attention" come first and are the only ones coloured. -->
      <div v-if="selected_dealer && tasks.length" class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span v-if="running_count" class="text-text-link font-medium">{{ $t('scheduled.admin.runningNow', running_count) }}</span>
        <span v-if="failing_count" class="text-danger font-medium">{{ $t('scheduled.admin.failingCount', failing_count) }}</span>
        <span v-if="paused_count" class="text-text-subtle">{{ $t('scheduled.admin.pausedCount', { count: paused_count }) }}</span>
        <span v-if="unknown_type_count" class="text-danger font-medium">{{ $t('scheduled.admin.unmappedType', { count: unknown_type_count }) }}</span>
        <span class="text-text-subtle">{{ $t('scheduled.admin.watcherSummary', { watchers: watcher_count, tasks: tasks.length }) }}</span>
      </div>

      <!-- Loading -->
      <div v-if="processing" class="px-2 py-6 text-sm text-text-subtle">{{ $t('scheduled.common.loading') }}</div>

      <!-- Empty -->
      <div v-else-if="selected_dealer && !visible_tasks.length" class="px-2 py-8 text-center text-sm text-text-subtle">
        {{ tasks.length ? $t('scheduled.admin.noWatchers') : $t('scheduled.admin.noTasks') }}
      </div>

      <!-- Inventory -->
      <div v-else-if="visible_tasks.length" class="flex flex-col gap-2">
        <div
          v-for="task in visible_tasks"
          :key="task.id"
          class="flex items-center gap-3 p-3 bg-surface-1 border border-border-separator rounded-md"
        >
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-text-default truncate">{{ task.name || $t('scheduled.admin.untitledTask') }}</span>
              <!-- Every task carries its type. Watchers keep the accent colour because they are what this
                   page is opened to inspect; the others are stated plainly rather than left blank. -->
              <span
                v-if="taskTypeLabel(task)"
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                :class="isWatcher(task) ? 'bg-brand-subtle text-text-link' : 'bg-surface-3 text-text-subtle'"
              >{{ taskTypeLabel(task) }}</span>
              <span
                v-if="hasUnknownType(task)"
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-3 text-danger flex-shrink-0"
                :title="$t('scheduled.admin.typeUnknownTooltip', { type: task.taskType })"
              >{{ $t('scheduled.admin.typeUnknown') }}</span>
              <span
                v-if="!task.enabled"
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-3 text-text-subtle flex-shrink-0"
              >{{ $t('scheduled.admin.state.paused') }}</span>
            </div>
            <div class="text-xs text-text-subtle truncate">{{ $t('scheduled.admin.owner', { owner: task.userId }) }}</div>
            <div class="text-xs text-text-subtle truncate">{{ scheduleSummary(task) }}</div>

            <!-- What the watcher actually watches. Without this the row shows a name and nothing else,
                 and a watcher IS its compiled condition — two tasks called "Aging ROs" can watch
                 different fields at different thresholds. -->
            <div v-if="isWatcher(task)" class="mt-1 text-xs text-text-default font-mono truncate"
                 :title="conditionSummary(task)">
              {{ conditionSummary(task) }}
            </div>

            <!-- Last / next run. Both live on the task document already; they were being discarded. -->
            <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-subtle">
              <span :title="absolute(task.lastRunAt)">{{ $t('scheduled.admin.lastRun', { value: relative(task.lastRunAt) }) }}<template v-if="task.lastRunStatus"> · {{ task.lastRunStatus }}</template></span>
              <span :title="absolute(task.trigger && task.trigger.nextRunAt)">{{ $t('scheduled.admin.nextRun', { value: relative(task.trigger && task.trigger.nextRunAt) }) }}</span>
            </div>

            <div v-if="task.pausedReason" class="mt-0.5 text-xs text-text-subtle truncate" :title="task.pausedReason">
              {{ $t('scheduled.admin.reasonLabel', { reason: task.pausedReason }) }}
            </div>
          </div>

          <!-- Derived state, right-aligned so the column scans vertically. -->
          <div class="flex-shrink-0 text-right">
            <span class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded" :class="stateClass(task)">
              {{ stateOf(task).label }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="row_message" class="text-xs text-text-subtle px-1">{{ row_message }}</div>
    </template>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import { getActiveLocaleTag } from '../../i18n/index.js';
import BriefFactApprovals from './BriefFactApprovals.vue';
import FusionDropdown from '@components/UI/FusionDropdown.vue';

// ScheduledTaskType, mirroring the C# enum in ScheduledTaskEnums.cs. The ORDINAL is what Cosmos stores,
// so this is append-only in exactly the same way the enum is: adding a row is safe, renumbering silently
// relabels every existing task document. Labels live in the i18n catalog under scheduled.common.taskType
// (resolved at render time so a language switch is reflected without a reload).
const TASK_TYPE_WATCHER = 1;

const TASK_TYPE_LABEL_KEYS = {
  0: 'scheduled.common.taskType.question',
  1: 'scheduled.common.taskType.watcher',
  2: 'scheduled.common.taskType.morningBrief',
};

// The same enum by name, because some writers persist the name rather than the ordinal.
const TASK_TYPE_ORDINALS = {
  ScheduledQuestion: 0,
  Watcher: 1,
  MorningBrief: 2,
};

export default {
  name: 'ScheduledAdmin',
  components: {
    'brief-fact-approvals': BriefFactApprovals,
    'fusion-dropdown': FusionDropdown,
  },
  emits: ['close'],
  data() {
    return {
      selected_dealer: '',
      tasks: [],
      watchers_only: false,
      processing: false,
      row_message: '',
    };
  },
  computed: {
    ...mapStores(useMainStore),
    is_fusion_admin() {
      return this.mainStore.is_fusion_admin;
    },
    dealer_options() {
      return [
        { value: '', label: this.$t('scheduled.admin.selectStore') },
        ...(this.dealers || []).map(d => ({ value: d.cifNumber, label: `${d.name || d.cifNumber} (${d.cifNumber})` })),
      ];
    },
    dealers() {
      return this.mainStore.all_dealers;
    },
    watcher_count() {
      return this.tasks.filter(t => this.isWatcher(t)).length;
    },
    unknown_type_count() {
      return this.tasks.filter(t => this.hasUnknownType(t)).length;
    },
    running_count() {
      return this.tasks.filter(t => this.stateOf(t).key === 'running').length;
    },
    failing_count() {
      return this.tasks.filter(t => this.stateOf(t).key === 'failing').length;
    },
    paused_count() {
      return this.tasks.filter(t => this.stateOf(t).key === 'paused').length;
    },
    // Problems first. Sorting by name would bury the one failing watcher among forty healthy ones,
    // and finding that watcher is the reason this page gets opened.
    visible_tasks() {
      const rank = { running: 0, failing: 1, paused: 2, idle: 3 };
      return this.tasks
        .filter(t => !this.watchers_only || this.isWatcher(t))
        .slice()
        .sort((a, b) => {
          const byState = rank[this.stateOf(a).key] - rank[this.stateOf(b).key];
          if (byState !== 0) return byState;
          return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
        });
    },
  },
  methods: {
    loadDealer() {
      if (!this.selected_dealer) {
        this.tasks = [];
        return;
      }
      const self = this;
      this.processing = true;
      this.mainStore.GetScheduledTasksForDealer(this.selected_dealer).then((response) => {
        self.tasks = Array.isArray(response) ? response : (response && response.tasks) || [];
        self.processing = false;
      }, () => {
        self.tasks = [];
        self.processing = false;
        self.flashMessage(self.$t('scheduled.admin.loadFailed'));
      });
    },
    setAllEnabled(enabled) {
      if (this.processing || !this.selected_dealer) return;
      const self = this;
      this.processing = true;
      this.mainStore.SetDealerScheduledTasksEnabled({ dealerId: this.selected_dealer, enabled }).then(() => {
        self.processing = false;
        self.flashMessage(self.$t(enabled ? 'scheduled.admin.enabledAll' : 'scheduled.admin.disabledAll'));
        self.loadDealer();
      }, () => {
        self.processing = false;
        self.flashMessage(self.$t('scheduled.admin.updateFailed'));
      });
    },
    flashMessage(msg) {
      const self = this;
      this.row_message = msg;
      setTimeout(() => { if (self.row_message === msg) self.row_message = ''; }, 4000);
    },
    // The ScheduledTaskType ordinal for a task, or null when the stored value maps to nothing we know.
    //
    // The enum arrives as a NAME from some writers and as a NUMBER from others — both shapes are in the
    // container today, so every comparison has to accept both. Comparing to the string alone (as this
    // page originally did) made a numerically-stored watcher render as an ordinary task with its
    // condition silently hidden.
    //
    // One table rather than a predicate per type, because the last time a type was added the page did
    // not learn about it: MorningBrief was appended to the C# enum and every brief in the store rendered
    // as "Type ?" — the page reported the newest feature as corrupt data. A single map is the thing you
    // cannot forget to update, and it is checked by ScheduledAdmin.spec.js against the enum's ordinals.
    taskTypeOf(task) {
      const t = task && task.taskType;
      if (t === null || t === undefined) return null;
      if (typeof t === 'number') return TASK_TYPE_LABEL_KEYS[t] === undefined ? null : t;
      const ordinal = TASK_TYPE_ORDINALS[t];
      return ordinal === undefined ? null : ordinal;
    },
    isWatcher(task) {
      return this.taskTypeOf(task) === TASK_TYPE_WATCHER;
    },
    // What to call this row. Every task gets a badge now: with three types in play, showing one only for
    // watchers left the reader to infer the rest, and "no badge" is indistinguishable from "type missing".
    taskTypeLabel(task) {
      const ordinal = this.taskTypeOf(task);
      return ordinal === null ? '' : this.$t(TASK_TYPE_LABEL_KEYS[ordinal]);
    },
    // A value that maps to nothing is worth showing rather than defaulting to "question": the server
    // makes the same comparison, so an unmapped type means the dispatcher is not treating it as a
    // watcher either.
    hasUnknownType(task) {
      const t = task && task.taskType;
      if (t === null || t === undefined) return false;
      return this.taskTypeOf(task) === null;
    },
    // Derived state, in priority order. "Paused" alone used to cover two very different situations —
    // an operator disabling a task and the scheduler parking one after repeated failures — so a broken
    // watcher was indistinguishable from an intentionally stopped one.
    stateOf(task) {
      if (!task) return { key: 'idle', label: this.$t('scheduled.admin.state.idle') };
      const leaseUntil = task.leaseUntil ? Date.parse(task.leaseUntil) : NaN;
      if (!Number.isNaN(leaseUntil) && leaseUntil > Date.now()) {
        return { key: 'running', label: this.$t('scheduled.admin.state.running') };
      }
      const failures = Number(task.consecutiveFailures) || 0;
      if (failures > 0) return { key: 'failing', label: this.$t('scheduled.admin.state.failing', { count: failures }) };
      if (!task.enabled) return { key: 'paused', label: this.$t('scheduled.admin.state.paused') };
      return { key: 'idle', label: this.$t('scheduled.admin.state.idle') };
    },
    stateClass(task) {
      const key = this.stateOf(task).key;
      if (key === 'running') return 'bg-brand-subtle text-text-link';
      if (key === 'failing') return 'bg-surface-3 text-danger';
      if (key === 'paused') return 'bg-surface-3 text-text-subtle';
      return 'bg-surface-2 text-text-subtle';
    },
    // The compiled condition in one line. Prefers the stored description, which is the restatement the
    // user confirmed at creation; falls back to rebuilding it from the parts.
    conditionSummary(task) {
      const c = task && task.condition;
      if (!c) return this.$t('scheduled.admin.noCondition');
      if (c.description) return c.description;
      const parts = [c.tool, c.filterField, this.comparatorSymbol(c.comparator), c.threshold]
        .filter(v => v !== null && v !== undefined && v !== '');
      return parts.length ? parts.join(' ') : this.$t('scheduled.admin.noCondition');
    },
    comparatorSymbol(comparator) {
      const map = {
        GreaterThan: '>', GreaterThanOrEqual: '>=',
        LessThan: '<', LessThanOrEqual: '<=',
        EqualTo: '=', NotEqualTo: '!=',
      };
      // Tolerates both shapes: the enum may arrive as a name or as its numeric value depending on the
      // serializer in front of it.
      if (typeof comparator === 'number') return Object.values(map)[comparator] || String(comparator);
      return map[comparator] || String(comparator || '');
    },
    // Relative rendering delegates to Intl.RelativeTimeFormat so the phrasing ("in 5 minutes",
    // "3 hours ago") follows the active locale. numeric:'always' — 'auto' would collapse
    // "1 day ago" to "yesterday" and sub-minute to "this minute", changing the English output this
    // page has always shown. The magnitude buckets stay explicit because the catalogue-backed unit
    // the raw API picks for multi-day deltas would not read as a watcher schedule. The spec asserts
    // the English "ago"/"in " shapes, which en-US output keeps.
    relative(value) {
      if (!value) return this.$t('scheduled.admin.never');
      const then = Date.parse(value);
      if (Number.isNaN(then)) return this.$t('scheduled.admin.never');
      const deltaMs = then - Date.now();
      const future = deltaMs > 0;
      const mins = Math.round(Math.abs(deltaMs) / 60000);
      let unit, amount;
      // The sub-minute bucket keeps this page's pre-i18n wording ("less than a minute ago") rather
      // than Intl's "this minute"/"in 0 minutes".
      if (mins < 1) return future ? this.$t('scheduled.admin.inLessThanAMinute') : this.$t('scheduled.admin.lessThanAMinuteAgo');
      if (mins < 60) { unit = 'minute'; amount = mins; }
      else if (mins < 1440) { unit = 'hour'; amount = Math.round(mins / 60); }
      else { unit = 'day'; amount = Math.round(mins / 1440); }
      return new Intl.RelativeTimeFormat(getActiveLocaleTag(), { numeric: 'always' })
        .format(future ? amount : -amount, unit);
    },
    absolute(value) {
      if (!value) return '';
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString(getActiveLocaleTag());
    },
    scheduleSummary(task) {
      const t = task && task.trigger;
      if (!t) return '';
      const cadence = t.cadence || 'Daily';
      // NPM-225: an interval has no clock time; the 07:00 it still holds is not when it runs.
      if (cadence === 'Interval') return this.$t('scheduled.common.cadenceInterval', { minutes: t.intervalMinutes ?? '?' });
      const time = t.time || '';
      // Same cadence label mapping ScheduledEdit/ScheduledManagement use, so the admin surface
      // does not show the raw enum where the other surfaces show the translated label.
      const keys = {
        Daily: 'scheduled.common.cadenceDaily',
        Weekdays: 'scheduled.common.cadenceWeekdays',
        Weekly: 'scheduled.common.cadenceWeekly',
        Monthly: 'scheduled.common.cadenceMonthly',
      };
      const parts = [keys[cadence] ? this.$t(keys[cadence]) : cadence];
      if (time) parts.push(this.$t('scheduled.admin.scheduleAt', { time }));
      return parts.join(' ');
    },
  },
};
</script>
